/** @jsx jsx */
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { withNamespaces } from 'react-i18next';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import Picture from '../../../ui/Picture';
import { formatPriceToCurrencyString } from '../../../ui/PriceGenerator';
import { CLOUDINARY_BASE_VALUES } from '../../../../utils/constants';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import * as dicts from '../../../../utils/dictionarykeys';
import * as styles from './index.styles.js';
import * as commonStyles from '../Recommendation/index.styles';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';

const iconImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  aspectRatio: '1:1',
  width: 18,
  height: 18,
};

const iconImageSizes = {
  [Breakpoints.xl]: iconImageSize,
  [Breakpoints.lg]: iconImageSize,
  [Breakpoints.md]: iconImageSize,
  [Breakpoints.sm]: iconImageSize,
};

const defaultData = {
  copy: {
    value: '',
  },
  features: [],
  services: [],
};

// Number of features that should be visible when list is collapsed
const featuresAboveTheFold = 4;

class FeaturesAndPricingPanel extends SafePureComponent {
  constructor(props) {
    super(props);

    const { features } = this.props;

    // Only use state when collapsable features are required
    if (features.length > featuresAboveTheFold) {
      this.state = {
        isFeaturesOpen: false,
      };
    }
  }

  toggleShowLessContent() {
    this.setState({
      isFeaturesOpen: !this.state.isFeaturesOpen,
    });
  }

  renderFeatures = () => {
    const { features, copy, t } = this.props;
    const isFeaturesOpen = get(this.state, 'isFeaturesOpen', null);

    return (
      <section css={[styles.section]}>
        <ul css={commonStyles.tags}>
          <li css={commonStyles.tag}>{t(dicts.SOLUTION_BUILDER.TAB1)}</li>
          <li css={commonStyles.tag}>{t(dicts.SOLUTION_BUILDER.TAB2)}</li>
        </ul>
        <h2 css={[styles.title, styles.titleFeatures]}>
          <Text field={copy} />
        </h2>
        {features.length > 0 && (
          <ul css={[styles.features, isFeaturesOpen && styles.featuresOpen]}>
            {features.map(({ fields }, index) => {
              const { Label, Icon } = fields;

              return (
                <li css={styles.feature} key={`feature-${index}`}>
                  <Picture
                    editable={false}
                    image={Icon}
                    imageSizes={iconImageSizes}
                    pictureCss={styles.featureIcon}
                  />
                  <Text field={Label} />
                </li>
              );
            })}
          </ul>
        )}
        {features.length > featuresAboveTheFold && (
          <button
            onClick={() => this.toggleShowLessContent()}
            css={[
              styles.featureToggle,
              isFeaturesOpen && styles.featureToggleClose,
            ]}
          >
            {t(
              isFeaturesOpen ? dicts.COMMONS.SEE_LESS : dicts.COMMONS.SEE_MORE,
            )}
            <i className="icon-chevron"></i>
          </button>
        )}
      </section>
    );
  };

  generateShortCurrencyDict = priceUnit => `${priceUnit}_SHORT`;

  renderServices = () => {
    const { t, services } = this.props;

    return (
      <section css={styles.section}>
        <h2 css={styles.title}>
          {t(dicts.SOLUTION_BUILDER.PRICE_RESULT_COPY)}
        </h2>
        <ul css={styles.services}>
          {services.map(({ price, currency, description }, index) => (
            <li css={styles.service} key={`service-${index}`}>
              <span css={styles.description}>{description}</span>
              <span css={styles.cost}>
                {t(currency)} {formatPriceToCurrencyString(price)}/
                {t(dicts.SOLUTION_BUILDER.MONTHTEXT)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  safeRender() {
    return (
      <div>
        {this.renderFeatures()}
        {this.renderServices()}
      </div>
    );
  }
}
const objectValueType = {
  value: PropTypes.string,
};

const featureType = {
  fields: PropTypes.shape({
    Icon: PropTypes.shape({
      value: PropTypes.shape({
        alt: PropTypes.string,
        height: PropTypes.string,
        src: PropTypes.string,
        width: PropTypes.string,
      }),
    }),
    Label: PropTypes.shape(objectValueType),
  }),
};

FeaturesAndPricingPanel.displayName = 'SolutionBuilderResults/FeaturesAndPricingPanel';

FeaturesAndPricingPanel.defaultProps = {
  ...defaultData,
  t: key => key,
};

FeaturesAndPricingPanel.propTypes = {
  copy: PropTypes.shape(objectValueType),
  features: PropTypes.arrayOf(PropTypes.shape(featureType)),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      price: PropTypes.string,
      currency: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  t: PropTypes.func,
};

export default withNamespaces()(FeaturesAndPricingPanel);
